<script setup>
import { ref, computed, watchEffect } from 'vue'
import { wingoImages } from '@/assets/wingoAssets'

const props = defineProps({
  socket: Object,
  onlinePlayers: Number,
  myBets: {
    type: Array,
    default: () => []
  },
  bets: {
    type: Array,
    default: () => []
  },
  authUser: Object
})

const activeTab = ref('all')
const currentPage = ref(1)
const pageSize = 10



const totalBet = computed(() =>
  (props.bets ?? []).reduce((sum, b) => sum + b.amount, 0)
)

const cryptoIcons = {
  BTC: new URL('@/assets/img/coins/BTC.svg', import.meta.url).href,
  ETH: new URL('@/assets/img/coins/ETH.svg', import.meta.url).href,
  BNB: new URL('@/assets/img/coins/BNB.svg', import.meta.url).href,
  DOGE: new URL('@/assets/img/coins/DOGE.svg', import.meta.url).href,
  USDT: new URL('@/assets/img/coins/USDT.svg', import.meta.url).href,
  Rivo: new URL('@/assets/img/coins/Rivo.svg', import.meta.url).href,
  TRX: new URL('@/assets/img/coins/TRX.svg', import.meta.url).href

}

function getFiatSymbol(currency) {
  const symbols = {
    TRY: '₺',
    USD: '$',
    EUR: '€',
    IDR: 'Rp',
    INR: '₹',
    CNY: '¥',
    BRL: 'R$'
  }
  return symbols[currency] || currency
}

const betTypeColor = (val) => {
  if (val === 'green') return 'green'
  if (val === 'red') return 'red'
  if (val === 'violet') return 'violet'
  return 'number'
}

const getWingoImage = (val) => wingoImages[val] || null

const groupedMyBets = computed(() => {
  const grouped = {}
  for (const bet of props.myBets) {
    const key = `${bet.roundId}-${bet.selection}`
    if (!grouped[key]) {
      grouped[key] = {
        roundId: bet.roundId,
        selection: bet.selection,
        amount: 0,
        profit: 0,
        wallet: bet.wallet
      }
    }
    grouped[key].amount += bet.amount
    grouped[key].profit += bet.profit
  }
  return Object.values(grouped)
})

const totalPages = computed(() =>
  Math.ceil(groupedMyBets.value.length / pageSize)
)

const paginatedMyBets = computed(() => {
  const start = (currentPage.value - 1) * pageSize
  return groupedMyBets.value.slice(start, start + pageSize)
})

const nextPage = () => {
  if (currentPage.value < totalPages.value) currentPage.value++
}
const prevPage = () => {
  if (currentPage.value > 1) currentPage.value--
}


</script>

<template>
  <div class="player-bets-wrapper">
    <!-- TABS -->
    <div class="tabs">
      <div :class="['tab', { active: activeTab === 'all' }]" @click="activeTab = 'all'">
        {{ $t('wingo.allBets') }}
      </div>
      <div :class="['tab', { active: activeTab === 'mine' }]" @click="activeTab = 'mine'">
        {{ $t('wingo.myBets') }}
      </div>
    </div>

    <!-- INFO BAR -->
    <div v-if="activeTab === 'all'" class="info-bar">
      <div class="info-item"><i class="fa fa-user"></i> {{ onlinePlayers }}</div>
      <div class="info-item"><i class="fa fa-coins"></i> {{ totalBet }} ₺</div>
    </div>

    <!-- BETS LIST -->
    <div class="bets-list">

      <!-- HEADER -->
      <div class="bets-header" v-if="activeTab === 'all'">
        <div class="header-col">{{ $t('wingo.user') }}</div>
        <div class="header-col">{{ $t('wingo.bet') }}</div>
        <div class="header-col">{{ $t('wingo.amount') }}</div>
      </div>

      <div class="bets-header" v-if="activeTab === 'mine'">
        <div class="header-col">{{ $t('wingo.bets') }}</div>
        <div class="header-col">{{ $t('wingo.amount') }}</div>
        <div class="header-col">{{ $t('wingo.profit') }}</div>
        <div class="header-col">{{ $t('wingo.round') }}</div>
      </div>

      <!-- ALL BETS -->
      <div v-show="activeTab === 'all'">
        <div v-if="bets.length === 0" class="no-bets">{{ $t('wingo.noBetsYet') }}</div>
        <div v-for="(bet, i) in bets" :key="'all-' + i" class="bet-entry all-entry">
          <div class="username">
            <img v-if="bet.avatar" :src="bet.avatar" alt="avatar" class="avatar-img" />
            {{ bet.username === authUser?.username ? $t('wingo.you') : '...' }}
          </div>
          <div class="bet-type" :class="betTypeColor(bet.selection)">
            <img :src="getWingoImage(bet.selection)" class="wingo-img" />
          </div>
          <div class="bet-amount" :style="{ color: bet.profit === null ? '#fff' : '' }">
            <img
              v-if="cryptoIcons[bet.wallet?.coinType]"
              :src="cryptoIcons[bet.wallet.coinType]"
              class="coin-icon"
              alt="coin"
            />
            {{ bet.amount }} {{ getFiatSymbol(bet.wallet?.currency) }}
            <i v-if="bet.profit === null" class="fa fa-spinner fa-spin" style="margin-left: 6px;" />
          </div>
        </div>
      </div>

      <!-- MY BETS -->
      <div v-show="activeTab === 'mine'">
        <div v-if="paginatedMyBets.length === 0" class="no-bets">{{ $t('wingo.noBetsPlaced') }}</div>
        <div v-for="(bet, i) in paginatedMyBets" :key="'mine-' + i" class="bet-entry mine-entry">
          <!-- BET IMG -->
          <div class="bet-type" :class="betTypeColor(bet.selection)">
            <img :src="getWingoImage(bet.selection)" class="wingo-img" />
          </div>

          <!-- AMOUNT -->
          <div class="bet-amount" :style="{ color: bet.profit === null ? '#fff' : '' }">
            <img
              v-if="cryptoIcons[bet.wallet?.coinType]"
              :src="cryptoIcons[bet.wallet.coinType]"
              class="coin-icon"
              alt="coin"
            />
            {{ bet.amount }} {{ getFiatSymbol(bet.wallet?.currency) }}
            <i v-if="bet.profit === null" class="fa fa-spinner fa-spin" style="margin-left: 6px;" />
          </div>

          <!-- PROFIT -->
          <div class="bet-profit" :class="{ green: bet.profit > 0, red: bet.profit < 0 }">
            <span v-if="bet.profit !== null">
              {{ bet.profit > 0 ? '+' : '' }}{{ bet.profit }} {{ bet.wallet?.currency || '₺' }}
            </span>
          </div>

          <!-- ROUND -->
          <div class="bet-round">
            <i class="fa fa-clock" style="margin-right: 4px; opacity: 0.6;" />
            <span class="round-text">{{ bet.roundId }}</span>
          </div>
        </div>

        <!-- PAGINATION -->
        <div class="pagination">
          <button @click="prevPage" :disabled="currentPage === 1">←</button>
          <span>{{ currentPage }} / {{ totalPages }}</span>
          <button @click="nextPage" :disabled="currentPage === totalPages">→</button>
        </div>
      </div>
    </div>
  </div>
</template>


<style scoped>
.player-bets-wrapper {
  background: var(--background);
  border-radius: 5px;
  color: #eee;
  height: 100%;
  overflow-y: auto;
  padding: 0;
  display: flex;
  flex-direction: column;
}

.tabs {
  display: flex;
  justify-content: space-between;
  background: var(--background);
}
.tab {
  flex: 1;
  padding: 0.6rem;
  text-align: center;
  cursor: pointer;
  font-weight: bold;
  font-size: 14px;
  transition: all 0.2s;
}
.tab.active {
 background: var(--buttonyellow);
    box-shadow: var(--buttonyellowbox);
    color: #fff;
    text-shadow: -1px -1px 0 var(--roo-btn-text-stroke-color, #C56A18), 1px -1px 0 var(--roo-btn-text-stroke-color, #C56A18), -1px 1px 0 var(--roo-btn-text-stroke-color, #C56A18), 1px 1px 0 var(--roo-btn-text-stroke-color, #C56A18);
}

.info-bar {
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 1rem;
  background: var(--background2);
  font-size: 13px;
  color: #ccc;
}
.info-item {
  display: flex;
  align-items: center;
  gap: 0.3rem;
}

.bets-list {
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem 1rem;
}

.bets-header {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  padding: 12px 16px;
  margin: 0 -10px;
  background: var(--background2);
  font-size: 10px;
  font-weight: bold;
  color: #999;
  text-transform: uppercase;
  border-radius: 7px;
}

.header-col {
  text-align: center;
}

.bet-entry {
  display: grid;
  align-items: center;
  padding: 0.3rem 0;
  column-gap: 12px;
  font-size: 10px;
  font-weight: bold;
}

.all-entry {
  grid-template-columns: 1fr 40px 1fr;
}

.mine-entry {
  grid-template-columns: repeat(4, 1fr);
}

.avatar-img {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 6px;
}

.username {
  font-size: 10px;
  font-weight: bold;
  text-align: left;
  display: flex;
  align-items: center;
}

.bet-type {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 35px;
}

.wingo-img {
  width: 16px;
  height: 16px;
  object-fit: contain;
}

.no-bets {
  font-size: 10px;
  color: #666;
  text-align: center;
  padding: 1rem 0;
}

.bet-amount,
.bet-profit,
.bet-round {
  text-align: center;
}

.bet-profit {
  font-weight: bold;
}

.bet-round {
  font-size: 10px;
  color: #aaa;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
}

.green {
  color: #4caf50;
}
.red {
  color: #f44336;
}

.pagination {
  margin-top: 10px;
  display: flex;
  justify-content: center;
  gap: 12px;
  font-size: 14px;
}

.coin-icon {
  width: 16px;
  height: 16px;
  margin-right: 4px;
  vertical-align: middle;
}
</style>
